/**
 * Typed fetch client for the provisioning API.
 * All responses use the { success, data, error } envelope; this module
 * unwraps that envelope and throws `ApiRequestError` on any failure so
 * callers can use plain try/catch.
 */
import type { ApiEnvelope, SessionExchangeData, UserProfile, UserSearchResult } from "./types";

export class ApiRequestError extends Error {
  public readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
  }
}

/** Base path is configurable via VITE_API_BASE; defaults to "/api". */
export function getApiBase(): string {
  const base = import.meta.env?.VITE_API_BASE;
  return (typeof base === "string" && base.length > 0 ? base : "/api").replace(/\/$/, "");
}

interface RequestOptions {
  method?: "GET" | "POST";
  body?: unknown;
  token?: string;
  signal?: AbortSignal;
  baseOverride?: string;
}

/**
 * Perform a request and unwrap the API envelope. Throws ApiRequestError on
 * network failure, non-2xx, malformed body, or `success: false`.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const base = options.baseOverride ?? getApiBase();
  const url = `${base}${path}`;

  const headers: Record<string, string> = { Accept: "application/json" };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (options.token) headers["Authorization"] = `Bearer ${options.token}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method ?? "GET",
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  } catch (err) {
    throw new ApiRequestError(
      err instanceof Error ? `Network error: ${err.message}` : "Network error",
      0,
    );
  }

  let envelope: ApiEnvelope<T> | null = null;
  try {
    envelope = (await response.json()) as ApiEnvelope<T>;
  } catch {
    // Fall through to status-based error below.
  }

  if (!envelope || typeof envelope.success !== "boolean") {
    throw new ApiRequestError(
      `Unexpected response (HTTP ${response.status})`,
      response.status,
    );
  }

  if (!envelope.success) {
    throw new ApiRequestError(envelope.error || `Request failed (HTTP ${response.status})`, response.status);
  }

  return envelope.data;
}

/** Exchange the one-time session token (`?s=`) for Matrix credentials. */
export function exchangeSession(
  s: string,
  signal?: AbortSignal,
): Promise<SessionExchangeData> {
  return apiRequest<SessionExchangeData>("/session/exchange", {
    method: "POST",
    body: { s },
    signal,
  });
}

/** Search users by email/name. Requires the Matrix access token as Bearer. */
export function searchUsers(
  query: string,
  token: string,
  signal?: AbortSignal,
): Promise<UserSearchResult[]> {
  const q = encodeURIComponent(query);
  return apiRequest<UserSearchResult[]>(`/users/search?q=${q}`, { token, signal });
}

/**
 * Fetch profiles (incl. company) for a set of matrix user ids. Requires the
 * caller's Matrix access token as Bearer. Empty input short-circuits.
 */
export function getProfiles(
  ids: string[],
  token: string,
  signal?: AbortSignal,
): Promise<UserProfile[]> {
  if (ids.length === 0) return Promise.resolve([]);
  const param = encodeURIComponent(ids.join(","));
  return apiRequest<UserProfile[]>(`/users/profiles?ids=${param}`, { token, signal });
}
